import { Progress } from "@/components/ui/progress";

interface AnalysisCategoryItemProps {
  category: {
    title: string;
    score?: number;
    content: string | string[] | object;
    key: string;
  };
}

export const AnalysisCategoryItem = ({ category }: AnalysisCategoryItemProps) => {
  // Helper function to safely render content
  const renderSafeContent = (content: any): string => {
    if (content === null || content === undefined) {
      return '';
    }
    
    if (typeof content === 'string') {
      return content;
    }
    
    if (typeof content === 'number') {
      return content.toString();
    }
    
    if (Array.isArray(content)) {
      return content.map(item => renderSafeContent(item)).join(', ');
    }
    
    if (typeof content === 'object') {
      // If it's an object, stringify it or extract its value
      if (content.value || content.description || content.content || content.text) {
        return renderSafeContent(content.value || content.description || content.content || content.text);
      }
      
      // Convert object to string if it can't be directly rendered
      return JSON.stringify(content);
    }
    
    return String(content);
  };

  // Format lists if content is an array
  const formatContent = (content: any) => {
    if (content === null || content === undefined) {
      return <p>No data available</p>;
    }
    
    if (Array.isArray(content)) {
      return (
        <ul className="list-disc pl-5 space-y-1">
          {content.map((item, index) => (
            <li key={index}>{renderSafeContent(item)}</li>
          ))}
        </ul>
      );
    }

    if (typeof content === 'object' && !Array.isArray(content)) {
      return renderSafeContent(content);
    }
    
    // Handle multi-line text with proper formatting
    return String(content).split('\n\n').map((paragraph, idx) => (
      <p key={idx} className="mb-2">{paragraph}</p>
    ));
  };

  return (
    <div className="border rounded-md bg-white border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between w-full">
          <span className="text-lg font-medium text-gray-800">{category.title}</span>
          {category.score !== undefined && (
            <div className="flex items-center space-x-3">
              <Progress 
                value={category.score} 
                className="w-24 h-2 bg-gray-100"
              />
              <span className="text-lg font-medium text-blue-600">{Math.round(category.score)}%</span>
            </div>
          )}
        </div>
      </div>
      <div className="px-4 py-3 bg-white text-gray-700">
        {formatContent(category.content)}
      </div>
    </div>
  );
};